import React, { useEffect, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { compose } from 'redux';
import { withRouter } from 'react-router-dom';
import { arrayOf, bool, func, number, object, string } from 'prop-types';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import classNames from 'classnames';
import {
    ERROR_CODE_INVALID_TYPE_IMAGE,
    ERROR_CODE_SIZE_EXCEEDED_IMAGE,
    propTypes,
} from '../../util/types';
import { ensureListing, ensureTransaction, ensureUser } from '../../util/data';
import { trimDisplayNameLastWord } from '../../util/text';
import { organizeItems } from '../../util/transaction';
import {
    // BookingPanel, // TODO check if it is used
    NamedLink,
    AvailableUnlocksModal,
    TransactionQuestionSet,
    Tooltip,
    MatchingDialog,
} from '../../components';
import { getAssetErrorMessageFromCode, resolveStateDataFn } from './TransactionPanel.helper';
import { SendMessageForm } from '../../forms';

// These are internal components that make this file more readable.
// import BreakdownMaybe from './BreakdownMaybe';
import FeedSection from './FeedSection';
import { TransactionPanelProvider } from './TransactionPanelContext';
import TransactionPanelNav from './TransactionPanelNav';
import AcceptDeclineRequestButtonsMaybe from './AcceptDeclineRequestButtonsMaybe';
// import SaleButtonsModal from './SaleButtonsModal';
import css from './TransactionPanel.css';
import { ProtectionInfoIcon } from '../../icons';
import {
    consultChatRequestMixpanel,
    respondOnCustomerInquiry as respondOnCustomerInquiryThunk,
} from '../../containers/TransactionPage/TransactionPage.duck';
import CreditsPurchasingModal from '../CreditsPurchasingModal/CreditsPurchasingModal';
import { CREDITS_NUM_TO_UNLOCK_PAGE } from '../../containers/TransactionPage/TransactionPage.duck';
import SponsoredChatLabel from './SponsoredChatLabel';
import { useFormFocusForSafari } from './useFormFocusForSafari';
import { useScrollToBottom } from './useScrollToBottom';
import { ACCEPT_ATTR } from '../../forms/SendMessageForm/SendMessageForm';
import { sendFile as sendFileThunk } from '../../containers/InboxPage/InboxPage.duck';

const acceptedFileTypes = ACCEPT_ATTR.split(',').map(type => type.toLowerCase());

const TransactionPanel = props => {
    const {
        otherParty,
        rootClassName,
        className,
        containerRootClassName,
        currentUser,
        savePaymentMethodFailed,
        sendMessageError,
        intl,
        timeSlots,
        fetchTimeSlotsError,
        navSectionClassName,
        inquiryError,
        asidePanelVisible,
        setAsidePanelVisibility,
        setInboxPanelVisible,
        isMobile,
        onInitialRender,
        pageId,
        allUserMessages,
        matchingScore,
        matchingMatrixLoading,
        matchingViewerListing,
        matchingCurrentPageListing,
        ...rest
    } = props;
    const dispatch = useDispatch();
    const onRespondOnCustomerInquiry = (txData, declineDataMaybe) =>
        dispatch(respondOnCustomerInquiryThunk(txData, declineDataMaybe));

    const onSendFile = (txId, file) => dispatch(sendFileThunk(txId, file));

    const fetchMessagesInProgress = useSelector(s => s['InboxPage'].fetchMessagesInProgress);
    const sendFileError = useSelector(s => s['InboxPage'].sendFileError);

    const [spendCreditsModalVisible, setSpendCreditsModalVisible] = useState(false);
    const [purchaseCreditsModalVisible, setPurchaseCreditsModalVisible] = useState(false);
    const [purchaseCreditsError, setPurchaseCreditsError] = useState(null);
    const [imageUploadRequested, setImageUploadRequested] = useState(false);
    const [dragOverFORM, setDragOverFORM] = useState(false);
    const [formError, setFormError] = useState(null);
    const [containerRef, setContainerRef] = useState(null);
    const [matchingModalOpen, setMatchingModalOpen] = useState(false);

    const [onSendMessageFormFocus] = useFormFocusForSafari();
    const ensuredTransaction = ensureTransaction(rest.transaction);
    const currentListing = ensureListing(ensuredTransaction.listing);
    const currentProvider = ensureUser(ensuredTransaction.provider);
    const currentCustomer = ensureUser(ensuredTransaction.customer);
    const { id: txId } = ensuredTransaction;
    const {
        attributes: {
            profile: {
                publicData: { userType: currentUserType },
            },
        },
    } = currentUser || { id: { uuid: null } };

    const isCustomer = currentCustomer?.id?.uuid === currentUser?.id?.uuid;
    const isProvider = currentProvider?.id?.uuid === currentUser?.id?.uuid;
    const isSponsored = ensuredTransaction?.attributes?.protectedData?.isSponsored;

    const transactionId = txId?.uuid || pageId;
    const messages = allUserMessages?.[transactionId] || [];
    const appointmentEntities = rest.appointmentEntities || {};
    const hasOlderMessages = rest.totalMessagePages > rest.oldestMessagePageFetched;
    const organizedItems = React.useMemo(
        () =>
            organizeItems(
                messages,
                ensuredTransaction.attributes.transitions || [],
                appointmentEntities[transactionId],
                hasOlderMessages || fetchMessagesInProgress
            ),
        [
            messages,
            ensuredTransaction.attributes.transitions,
            appointmentEntities,
            transactionId,
            hasOlderMessages,
            fetchMessagesInProgress,
        ]
    );

    const lastMessage = organizedItems[organizedItems.length - 1];
    const lastMessageId = lastMessage?._id || lastMessage?.id?.uuid;

    const {
        isAnonym,
        publicData: { userType: otherPartyUserType },
    } = otherParty.attributes.profile;

    const otherPartyId = (otherParty.id || {}).uuid;
    const otherPartyDisplayName = trimDisplayNameLastWord(
        otherParty.attributes.profile.displayName || ''
    );
    const scrollToContainer = useScrollToBottom(containerRef);

    useEffect(() => {
        if (txId) onInitialRender(txId);
    }, [txId]);

    useEffect(() => {
        if (lastMessageId) {
            scrollToContainer();
        }
    }, [lastMessageId]);

    useEffect(() => {
        if (sendFileError?.code) {
            setFormError(getAssetErrorMessageFromCode(sendFileError.code));
        }
    }, [sendFileError?.code]);

    const transactionRolePublicData = isCustomer
        ? currentProvider.attributes.profile.publicData
        : currentCustomer.attributes.profile.publicData;

    if (transactionRolePublicData) {
        delete transactionRolePublicData['auto'];
        delete transactionRolePublicData['drivingLicense'];
        delete transactionRolePublicData['language'];
    }

    const noListingInfo = currentListing && !currentListing.id;
    const isListingDeleted = !noListingInfo && currentListing.attributes.deleted;
    const isCustomerLoaded = !!currentCustomer.id;
    const isCustomerBanned = isCustomerLoaded && currentCustomer.attributes.banned;
    const isCustomerDeleted = isCustomerLoaded && currentCustomer.attributes.deleted;
    const isProviderLoaded = !!currentProvider.id;
    const isProviderBanned = isProviderLoaded && currentProvider.attributes.banned;
    const isProviderDeleted = isProviderLoaded && currentProvider.attributes.deleted;
    /** listing deleted */

    const stateData = resolveStateDataFn({
        currentTransaction: ensuredTransaction,
        isProvider,
        currentUser,
    });

    const { publicData: listingPublicData } = currentListing.attributes;

    const {
        showQuestionsSet,
        hideSendMessagePanel,
        showAcceptDeclineRequestButtons,
        showSponsoredLabel,
        showMessageNotification,
        showStatusPanel,
        hideFooter,
    } = stateData;

    const showSendMessageForm =
        // !listingIsClosed &&
        // !otherPartyIsNotAvailable &&
        !hideSendMessagePanel &&
        !isCustomerBanned &&
        !isCustomerDeleted &&
        !isProviderBanned &&
        !isProviderDeleted;

    const sendMessagePlaceholder = intl.formatMessage(
        { id: 'TransactionPanel.sendMessagePlaceholder' },
        { name: otherPartyDisplayName }
    );

    const paymentMethodsPageLink = (
        <NamedLink name="PaymentMethodsPage">
            <FormattedMessage id="TransactionPanel.paymentMethodsPageLink" />
        </NamedLink>
    );

    const classes = classNames(rootClassName || css.root, className);

    const labelClasses = classNames(
        css.sendMessageForm,
        dragOverFORM ? css.sendMessageFormHovered : ''
    );

    const customerName = trimDisplayNameLastWord(currentCustomer.attributes.profile.displayName);
    const providerName = trimDisplayNameLastWord(currentProvider.attributes.profile.displayName);

    const containerClassNames = classNames({
        [css.container]: true,
        [css.containerNoFooter]: hideFooter,
        [containerRootClassName]: !!containerRootClassName,
    });

    const { ridingsNum } = listingPublicData || {};
    const days = ridingsNum == 1 ? 'Tag' : 'Tagen';
    const ridingsNumTooltipContent = intl.formatMessage(
        {
            id: 'TransactionPanel.ridingsNumTooltipContent',
        },
        { ridingsNum, days }
    );
    const classesContainer = classNames(containerClassNames, {
        [css.containerWithoutShowStatusPanel]: !showStatusPanel,
    });

    const respondOnCustomerInquiry = async (declineMessageMaybe, reason) => {
        const {
            attributes: {
                email,
                profile: { firstName, lastName },
            },
        } = currentUser;
        const isDeclineAction = declineMessageMaybe && reason;

        const response = await onRespondOnCustomerInquiry(
            {
                txId,
                email,
                lastName,
                firstName,
                otherParty,
                currentListing,
            },
            isDeclineAction ? { declineMessage: declineMessageMaybe, reason } : {}
        );

        if (response) {
            // reload the page
            window.location.reload();
        }
    };

    const handleAcceptSponsoredOrDeclineRequest = (declineMessageMaybe, reason) => {
        if (declineMessageMaybe) {
            return respondOnCustomerInquiry(declineMessageMaybe, reason);
        }

        if (isSponsored) {
            // do not show credits modal in case of sponsored transaction
            return respondOnCustomerInquiry();
        }
        const {
            attributes: {
                profile: {
                    publicData: { userType },
                    privateData: { creditsAvailable = [] },
                },
            },
        } = currentUser;

        const creditsAvailableByType = Number(creditsAvailable[userType] || 0);

        if (creditsAvailableByType < CREDITS_NUM_TO_UNLOCK_PAGE) {
            setPurchaseCreditsModalVisible(true);
        } else {
            setSpendCreditsModalVisible(true);
        }
    };

    const handleAcceptRequest = async () => {
        setSpendCreditsModalVisible(false);
        await respondOnCustomerInquiry();
    };

    const onMessageSubmit = ({ message: messageValue }, form) => {
        let textareaValue = null;
        let eventTextAreaTarget = null;

        try {
            if (typeof window !== 'undefined') {
                eventTextAreaTarget = window.event.target.getElementsByTagName('textarea')[0];
                textareaValue = eventTextAreaTarget.value;
            }
        } catch (e) {}

        const message = textareaValue ? textareaValue : messageValue ? messageValue.trim() : null;

        if (!message) {
            return;
        }

        const { id } = ensuredTransaction;

        rest.onSendMessage(id, message)
            .then(() => {
                if (eventTextAreaTarget) {
                    eventTextAreaTarget.value = '';
                }
                /* eslint-disable no-unused-expressions */
                form?.reset();
            })
            .catch(e => {
                // Ignore, Redux handles the error
            });
    };

    const onImageUploadHandler = files => {
        const file = files?.[0];

        if (typeof files !== 'object' || !file || files.length > 1 || imageUploadRequested) {
            const formError = imageUploadRequested
                ? 'File loading is in progress.'
                : 'File either has an invalid type, or the total number of files is bigger than 1.';

            return setFormError(formError);
        }

        const fileType = file.type?.toLowerCase() || '';
        const fileExtension = file.name?.match(/\.[^.]+$/)?.[0]?.toLowerCase() || '';
        const isAcceptedFile = acceptedFileTypes.some(acceptedType =>
            acceptedType.endsWith('/*')
                ? fileType.startsWith(acceptedType.slice(0, -1))
                : acceptedType.startsWith('.')
                ? fileExtension === acceptedType
                : fileType === acceptedType
        );

        if (!isAcceptedFile) {
            return setFormError(getAssetErrorMessageFromCode(ERROR_CODE_INVALID_TYPE_IMAGE));
        }

        const fileSizeExceeded = files[0].size / 1024 ** 2 > 10;

        if (fileSizeExceeded) {
            return setFormError(getAssetErrorMessageFromCode(ERROR_CODE_SIZE_EXCEEDED_IMAGE));
        }

        setImageUploadRequested(true);
        setFormError(null);

        onSendFile(ensuredTransaction, files[0]).finally(() => {
            setImageUploadRequested(false);
        });
    };

    const onDragHandler = (e, flag) => {
        // function responsible for changing label background
        e.preventDefault();
        e.stopPropagation();
        setDragOverFORM(flag);
    };

    const onDropHandler = (e, form) => {
        setDragOverFORM(false);

        const droppedFiles = e.dataTransfer && e.dataTransfer.files;

        if (!droppedFiles) {
            return setFormError('Selected file is invalid.');
        }

        onImageUploadHandler(droppedFiles);
    };

    return (
        <TransactionPanelProvider
            messages={messages}
            appointmentEntities={appointmentEntities}
            organizedItems={organizedItems}
            formError={formError}
        >
            <>
                <TransactionPanelNav
                    navSectionClassName={navSectionClassName}
                    history={rest.history}
                    otherParty={otherParty}
                    isAnonym={isAnonym}
                    otherPartyUserType={otherPartyUserType}
                    otherPartyId={otherPartyId}
                    matchingMatrixLoading={matchingMatrixLoading}
                    matchingScore={matchingScore}
                    setMatchingModalOpen={setMatchingModalOpen}
                    noListingInfo={noListingInfo}
                    isListingDeleted={isListingDeleted}
                    asidePanelVisible={asidePanelVisible}
                    setAsidePanelVisibility={setAsidePanelVisibility}
                />
                <div className={classes}>
                    <div className={classesContainer} ref={setContainerRef}>
                        <div className={css.txInfo}>
                            <aside className={css.dataProtectionSidenote}>
                                <ProtectionInfoIcon />
                                <FormattedMessage
                                    id="TransactionPanel.dataProtectionSidenote"
                                    values={{
                                        displayName: trimDisplayNameLastWord(
                                            otherPartyDisplayName || ''
                                        ),
                                    }}
                                />
                            </aside>
                            {ridingsNum && (
                                <Tooltip
                                    content={ridingsNumTooltipContent}
                                    tooltipClassName={css.tooltip}
                                >
                                    <div className={css.ridingsNumButton} data-tooltip={'item'}>
                                        <FormattedMessage
                                            id="EditListingDescriptionForm.numberOfRidings"
                                            values={{ value: ridingsNum }}
                                        />
                                    </div>
                                </Tooltip>
                            )}

                            {savePaymentMethodFailed ? (
                                <p className={css.genericError}>
                                    <FormattedMessage
                                        id="TransactionPanel.savePaymentMethodFailed"
                                        values={{ paymentMethodsPageLink }}
                                    />
                                </p>
                            ) : null}

                            <FeedSection
                                currentTransaction={ensuredTransaction}
                                currentUser={currentUser}
                                {...rest}
                            />

                            {isSponsored && showSponsoredLabel && (
                                <SponsoredChatLabel
                                    otherParty={otherParty}
                                    isCustomer={isCustomer}
                                />
                            )}

                            {showAcceptDeclineRequestButtons && (
                                <AcceptDeclineRequestButtonsMaybe
                                    onRespondOnCustomerInquiry={
                                        handleAcceptSponsoredOrDeclineRequest
                                    }
                                    revieweeName={otherPartyDisplayName}
                                    currentListing={currentListing}
                                    inquiryError={inquiryError}
                                    currentUser={currentUser}
                                    otherParty={otherParty}
                                    transaction={ensuredTransaction}
                                    key={ensuredTransaction?.id?.uuid}
                                />
                            )}
                            {showMessageNotification && (
                                <p className={css.showMessageNotification}>
                                    <FormattedMessage
                                        id="TransactionPanel.showMessageNotification"
                                        values={{ displayName: otherPartyDisplayName }}
                                    />
                                </p>
                            )}

                            {showSendMessageForm && (
                                <React.Fragment>
                                    {purchaseCreditsError && (
                                        <span className={css.actionError}>
                                            Entschuldigen Sie, etwas ist schiefgelaufen. Versuchen
                                            Sie, die Seite neu zu laden oder wenden Sie sich an den
                                            Support.
                                        </span>
                                    )}
                                    <SendMessageForm
                                        formId="TransactionPanel.SendMessageForm"
                                        rootClassName={labelClasses}
                                        messagePlaceholder={sendMessagePlaceholder}
                                        sendMessageError={sendMessageError}
                                        otherParty={otherParty}
                                        onFocus={onSendMessageFormFocus}
                                        onBlur={() => null}
                                        onSubmit={onMessageSubmit}
                                        onDrag={onDragHandler}
                                        onDrop={onDropHandler}
                                        imageUploadRequested={imageUploadRequested}
                                        onImageUploadHandler={onImageUploadHandler}
                                        currentListing={currentListing}
                                        currentTransaction={ensuredTransaction}
                                        currentUser={currentUser}
                                        customerName={customerName}
                                        providerName={providerName}
                                        isCustomer={isCustomer}
                                        isMobile={isMobile}
                                        {...rest}
                                    />
                                </React.Fragment>
                            )}
                            {showQuestionsSet && (
                                <TransactionQuestionSet
                                    currentUserType={currentUserType}
                                    intl={intl}
                                />
                            )}
                            <div className="bottom" />
                        </div>
                    </div>

                    {spendCreditsModalVisible && (
                        <AvailableUnlocksModal
                            isOpen
                            onClose={() => setSpendCreditsModalVisible(false)}
                            creditsNum={CREDITS_NUM_TO_UNLOCK_PAGE}
                            modalHeading="Match bestätigen"
                            modalDesc="Nutze deine Credits, um diese Anfrage zu akzeptieren."
                            actionButtonText="Anfrage akzeptieren"
                            currentUser={currentUser}
                            handleAcceptOrDeclineRequest={handleAcceptRequest}
                        />
                    )}
                </div>
                {purchaseCreditsModalVisible && (
                    <CreditsPurchasingModal
                        isOpen
                        heading="Alles aufgebraucht!"
                        description="Füge mehr Credits hinzu, um diese Anfrage zu akzeptieren."
                        sidenote={<p>Wähle die Anzahl Credits</p>}
                        currentUser={currentUser}
                        onClose={() => setPurchaseCreditsModalVisible(false)}
                        onError={() => {
                            setPurchaseCreditsModalVisible(false);
                            setPurchaseCreditsError();
                        }}
                    />
                )}
                {matchingModalOpen && (
                    <MatchingDialog
                        modalOpen
                        onClose={() => setMatchingModalOpen(false)}
                        currentPageListing={matchingCurrentPageListing}
                        viewerListing={matchingViewerListing}
                        scoreData={matchingScore}
                    />
                )}
            </>
        </TransactionPanelProvider>
    );
};

TransactionPanel.propTypes = {
    rootClassName: string,
    className: string,

    currentUser: propTypes.currentUser,
    transaction: propTypes.transaction.isRequired,
    totalMessagePages: number.isRequired,
    oldestMessagePageFetched: number.isRequired,
    allUserMessages: object.isRequired,
    initialMessageFailed: bool,
    savePaymentMethodFailed: bool,
    fetchMessagesInProgress: bool.isRequired,
    fetchMessagesError: propTypes.error,
    sendMessageError: propTypes.error,
    sendReviewError: propTypes.error,
    onSendMessage: func.isRequired,
    timeSlots: arrayOf(propTypes.timeSlot),
    fetchTimeSlotsError: propTypes.error,
    // from injectIntl
    intl: intlShape,
};

const mapDispatchToProps = dispatch => ({
    onInitialRender: txId => dispatch(consultChatRequestMixpanel(txId)),
});

export default compose(connect(null, mapDispatchToProps), withRouter, injectIntl)(TransactionPanel);
